import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Button,
    Chip,
    Avatar,
    Divider,
    Card,
    CardContent,
    Alert,
    Snackbar,
    CircularProgress,
    Stack,
    TextField,
    FormControlLabel,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Business as BusinessIcon,
    VerifiedUser as VerifiedUserIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Pending as PendingIcon,
    Shield as ShieldIcon,
    Description as DescriptionIcon,
    Upload as UploadIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    LocationOn as LocationIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import './Companies.css';

// Info Item
const InfoItem = ({ icon, label, value, color }) => (
    <Box className="companies-info-item" sx={{ mb: 2 }}>
        <Box sx={{ color: color, display: 'flex', alignItems: 'center' }}>
            {icon}
        </Box>
        <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={500}>{value || 'N/A'}</Typography>
        </Box>
    </Box>
);

const CompanyVerification = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [company, setCompany] = useState(null);
    const [verificationData, setVerificationData] = useState({
        status: 'pending',
        notes: '',
        documents: []
    });
    const [openRejectDialog, setOpenRejectDialog] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const pageRef = useRef(null);

    // Immersive scroll
    const { scrollYProgress } = useScroll({ target: pageRef, offset: ['start start', 'end start'] });
    const orbY1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
    const orbY2 = useTransform(scrollYProgress, [0, 1], [0, 90]);

    useEffect(() => {
        fetchCompany();
    }, [id]);

    const fetchCompany = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'companies', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();
                setCompany({ id: docSnap.id, ...data });
                setVerificationData({
                    status: data.isVerified ? 'approved' : 'pending',
                    notes: data.verificationNotes || '',
                    documents: data.verificationDocuments || []
                });
            } else {
                toast.error('Company not found');
                navigate('/companies');
            }
        } catch (error) {
            console.error('Error fetching company:', error);
            toast.error('Failed to load company');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async () => {
        setSaving(true);
        try {
            await updateDoc(doc(db, 'companies', id), {
                isVerified: true,
                verificationStatus: 'approved',
                verificationDate: serverTimestamp(),
                verifiedBy: user?.uid,
                verificationNotes: verificationData.notes,
                updatedAt: serverTimestamp()
            });
            showSnackbar('Company verified successfully!', 'success');
            fetchCompany();
        } catch (error) {
            console.error('Error verifying company:', error);
            showSnackbar('Failed to verify company', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleReject = async () => {
        setSaving(true);
        try {
            await updateDoc(doc(db, 'companies', id), {
                isVerified: false,
                verificationStatus: 'rejected',
                verificationDate: serverTimestamp(),
                verifiedBy: user?.uid,
                verificationNotes: rejectReason || 'Rejected',
                updatedAt: serverTimestamp()
            });
            showSnackbar('Company rejected!', 'warning');
            setOpenRejectDialog(false);
            setRejectReason('');
            fetchCompany();
        } catch (error) {
            console.error('Error rejecting company:', error);
            showSnackbar('Failed to reject company', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        setSaving(true);
        try {
            await updateDoc(doc(db, 'companies', id), {
                isVerified: false,
                verificationStatus: 'pending',
                verificationDate: null,
                verifiedBy: null,
                verificationNotes: '',
                updatedAt: serverTimestamp()
            });
            showSnackbar('Verification reset!', 'info');
            fetchCompany();
        } catch (error) {
            console.error('Error resetting verification:', error);
            showSnackbar('Failed to reset verification', 'error');
        } finally {
            setSaving(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const getStatusConfig = () => {
        if (company?.isVerified) {
            return {
                label: 'Verified',
                color: 'success',
                icon: <VerifiedUserIcon />,
                bgColor: '#e8f5e9',
                textColor: '#1f9d55'
            };
        } else if (company?.verificationStatus === 'rejected') {
            return {
                label: 'Rejected',
                color: 'error',
                icon: <CancelIcon />,
                bgColor: '#ffebee',
                textColor: '#d13a4f'
            };
        } else {
            return {
                label: 'Pending',
                color: 'warning',
                icon: <PendingIcon />,
                bgColor: '#fff3e0',
                textColor: '#c9820f'
            };
        }
    };

    const statusConfig = getStatusConfig();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }

    if (!company) {
        return (
            <Container>
                <Alert severity="error">Company not found</Alert>
            </Container>
        );
    }

    return (
        <Box className="companies-verification-page" ref={pageRef}>
            {/* Immersive Background */}
            <Box className="companies-background">
                <motion.div className="companies-bg-orb orb-1" style={{ y: orbY1 }} />
                <motion.div className="companies-bg-orb orb-2" style={{ y: orbY2 }} />
                <div className="companies-bg-orb orb-3" />
                <div className="companies-bg-grid" />
            </Box>

            <Container maxWidth="lg" className="companies-container">
                {/* Back Button */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/companies/${id}`)}
                        className="companies-back-btn"
                    >
                        Back to Company
                    </Button>
                </motion.div>

                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    <Paper className="companies-verification-header">
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Box display="flex" alignItems="center" gap={3}>
                                    <Avatar
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            bgcolor: 'var(--primary, #2E6F8E)',
                                            fontSize: 28,
                                            fontWeight: 700,
                                            boxShadow: '0 4px 20px color-mix(in srgb,var(--primary,#2E6F8E) 30%,transparent)'
                                        }}
                                    >
                                        {company.companyName?.charAt(0) || 'C'}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h5" fontWeight={700} sx={{ color: '#1a1a2e' }}>
                                            {company.companyName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Verification Management
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                                    <Chip
                                        icon={statusConfig.icon}
                                        label={statusConfig.label}
                                        sx={{
                                            py: 2,
                                            px: 2,
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            backgroundColor: statusConfig.bgColor,
                                            color: statusConfig.textColor,
                                            '& .MuiChip-icon': {
                                                color: statusConfig.textColor
                                            }
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </motion.div>

                {/* Company Info Cards */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 }}
                >
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card className="companies-stat-mini">
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">Type</Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {company.companyType?.replace('_', ' ').toUpperCase()}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card className="companies-stat-mini">
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">Size</Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {company.companySize}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card className="companies-stat-mini">
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">Headquarters</Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {company.headquarters || 'N/A'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card className="companies-stat-mini">
                                <CardContent>
                                    <Typography variant="caption" color="text.secondary">Joined</Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {company.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </motion.div>

                {/* Verification Content */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Paper className="companies-verification-paper">
                                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: '#1a1a2e' }}>
                                    Verification Details
                                </Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: '#1a1a2e' }}>
                                        Status
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                        <Chip
                                            icon={statusConfig.icon}
                                            label={statusConfig.label}
                                            sx={{
                                                py: 1.5,
                                                px: 1,
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                backgroundColor: statusConfig.bgColor,
                                                color: statusConfig.textColor,
                                                '& .MuiChip-icon': {
                                                    color: statusConfig.textColor
                                                }
                                            }}
                                        />
                                        {company.isVerified && (
                                            <Typography variant="caption" color="text.secondary">
                                                Verified on {company.verificationDate?.toDate?.()?.toLocaleDateString()}
                                            </Typography>
                                        )}
                                        {company.verificationStatus === 'rejected' && (
                                            <Typography variant="caption" color="text.secondary">
                                                Rejected on {company.verificationDate?.toDate?.()?.toLocaleDateString()}
                                            </Typography>
                                        )}
                                        {!company.isVerified && company.verificationStatus !== 'rejected' && (
                                            <Typography variant="caption" color="text.secondary">
                                                Awaiting verification
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: '#1a1a2e' }}>
                                        Notes
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        placeholder="Add verification notes..."
                                        value={verificationData.notes}
                                        onChange={(e) => setVerificationData({
                                            ...verificationData,
                                            notes: e.target.value
                                        })}
                                        className="companies-modern-field"
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: '#1a1a2e' }}>
                                        Documents
                                    </Typography>
                                    <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                                        <Typography variant="body2">
                                            Upload verification documents here (Business License, Tax ID, etc.)
                                        </Typography>
                                    </Alert>
                                    <Button
                                        variant="outlined"
                                        startIcon={<UploadIcon />}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Upload Documents
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Paper className="companies-verification-paper">
                                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: '#1a1a2e' }}>
                                    Actions
                                </Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Stack spacing={2}>
                                    {!company.isVerified && company.verificationStatus !== 'rejected' && (
                                        <>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                startIcon={<CheckCircleIcon />}
                                                onClick={handleVerify}
                                                disabled={saving}
                                                sx={{
                                                    borderRadius: 2,
                                                    bgcolor: '#1f9d55',
                                                    '&:hover': { bgcolor: '#156d3a' },
                                                    py: 1.5
                                                }}
                                            >
                                                {saving ? 'Verifying...' : 'Verify Company'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                startIcon={<CancelIcon />}
                                                color="error"
                                                onClick={() => setOpenRejectDialog(true)}
                                                disabled={saving}
                                                sx={{ borderRadius: 2, py: 1.5 }}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}

                                    {company.verificationStatus === 'rejected' && (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<PendingIcon />}
                                            onClick={handleReset}
                                            disabled={saving}
                                            sx={{
                                                borderRadius: 2,
                                                bgcolor: '#c9820f',
                                                '&:hover': { bgcolor: '#a3660c' },
                                                py: 1.5
                                            }}
                                        >
                                            {saving ? 'Resetting...' : 'Reset to Pending'}
                                        </Button>
                                    )}

                                    {company.isVerified && (
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<PendingIcon />}
                                            onClick={handleReset}
                                            disabled={saving}
                                            sx={{ borderRadius: 2, py: 1.5, color: '#c9820f', borderColor: '#c9820f' }}
                                        >
                                            {saving ? 'Resetting...' : 'Reset Verification'}
                                        </Button>
                                    )}

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={<SaveIcon />}
                                        onClick={() => {
                                            updateDoc(doc(db, 'companies', id), {
                                                verificationNotes: verificationData.notes,
                                                updatedAt: serverTimestamp()
                                            });
                                            showSnackbar('Notes saved!', 'success');
                                        }}
                                        disabled={saving}
                                        sx={{
                                            borderRadius: 2,
                                            bgcolor: 'var(--primary, #2E6F8E)',
                                            '&:hover': { bgcolor: 'var(--secondary, #42B89B)' },
                                            py: 1.5
                                        }}
                                    >
                                        Save Notes
                                    </Button>
                                </Stack>

                                <Divider sx={{ my: 3 }} />

                                <Typography variant="caption" color="text.secondary" display="block" align="center">
                                    Last updated: {company.updatedAt?.toDate?.()?.toLocaleString() || 'Never'}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </motion.div>
            </Container>

            {/* Reject Dialog */}
            <Dialog
                open={openRejectDialog}
                onClose={() => setOpenRejectDialog(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { className: 'companies-modern-dialog' } }}
            >
                <DialogTitle className="companies-dialog-title">
                    <Box display="flex" alignItems="center" gap={1}>
                        <CancelIcon sx={{ color: '#d13a4f' }} />
                        Reject Company
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body2" gutterBottom sx={{ color: '#1a1a2e' }}>
                            Please provide a reason for rejecting this company:
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Enter rejection reason..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            className="companies-modern-field"
                            required
                        />
                    </Box>
                </DialogContent>
                <DialogActions className="companies-dialog-actions">
                    <Button onClick={() => setOpenRejectDialog(false)} className="companies-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleReject}
                        disabled={saving}
                        startIcon={<CancelIcon />}
                        sx={{ borderRadius: 2 }}
                    >
                        {saving ? 'Rejecting...' : 'Reject Company'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    variant="filled"
                    sx={{ borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CompanyVerification;